import { useState } from 'react';

const QUESTIONS = [
  {
    id: 'fsm',
    q: 'Are you (or have you been in the last 6 years) in receipt of free school meals?',
  },
  {
    id: 'first_gen',
    q: 'Would you be the first in your immediate family to go to university?',
  },
  {
    id: 'low_income',
    q: 'Is your household income under £25,000 per year?',
  },
  {
    id: 'bursary_school',
    q: 'Do you attend (or did you attend) a state school in a widening-participation area, or hold a school bursary?',
  },
];

function band(score) {
  if (score >= 4) {
    return {
      label: '100% bursary likely',
      tone: '#166534',
      bg: '#dcfce7',
      verdict:
        "You meet all four widening-participation flags. Most applicants in your position receive the full 100% bursary — Career Camp ends up free.",
      cta: 'Book the free advisor call to confirm and reserve your place.',
    };
  }
  if (score === 3) {
    return {
      label: '75% bursary likely',
      tone: '#92400e',
      bg: '#fef3c7',
      verdict:
        "You meet three of the four widening-participation flags. A 75% bursary is the typical outcome for applicants with your profile.",
      cta: 'Book the free call to confirm and discuss your final share.',
    };
  }
  if (score === 2) {
    return {
      label: '50% bursary likely',
      tone: '#1e40af',
      bg: '#dbeafe',
      verdict:
        "Two flags puts you in the 50% bursary band for most cohorts. Bring documentation for the income and FSM flags to the advisor call.",
      cta: "Book a call so we can validate the documentation route.",
    };
  }
  if (score === 1) {
    return {
      label: 'Contextual review',
      tone: '#6b21a8',
      bg: '#f3e8ff',
      verdict:
        "One flag triggers a contextual review. Outcomes vary — sometimes 25–50%, sometimes none — depending on the cohort's bursary pool.",
      cta: 'Book a call to walk through the documentation and check current bursary availability.',
    };
  }
  return {
    label: 'Standard pricing applies',
    tone: '#4b4361',
    bg: '#f4f4f5',
    verdict:
      "No widening-participation flags tripped. Standard Career Camp pricing applies. Splits across instalments are available if needed.",
    cta: "Book a call to talk through pricing and instalment options.",
  };
}

export default function BursaryCheck() {
  const [answers, setAnswers] = useState({});
  const allAnswered = QUESTIONS.every((q) => answers[q.id] !== undefined);
  const yesCount = QUESTIONS.filter((q) => answers[q.id] === true).length;
  const result = allAnswered ? band(yesCount) : null;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #ece8f6',
        borderRadius: 14,
        padding: '20px 22px',
        boxShadow: '0 8px 24px rgba(20, 5, 60, 0.05)',
        marginBottom: 22,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: '#5b21b6',
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          marginBottom: 8,
        }}
      >
        Bursary self-check
      </div>
      <h2 style={{ margin: '0 0 6px', fontSize: 20, color: '#1f1240' }}>
        Is Career Camp free for you?
      </h2>
      <p style={{ margin: '0 0 16px', color: '#4b4361', fontSize: 14, lineHeight: 1.5 }}>
        Four quick yes/no questions. The result is indicative — the final bursary
        decision is made on documentation in your free 15-minute advisor call.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {QUESTIONS.map((q, idx) => (
          <div
            key={q.id}
            style={{
              border: '1px solid #ece8f6',
              borderRadius: 12,
              padding: '12px 14px',
              background: '#fafafa',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: answers[q.id] !== undefined ? '#5b21b6' : '#d8d6e6',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  marginTop: 2,
                }}
              >
                {idx + 1}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, color: '#1f1240', marginBottom: 8 }}>{q.q}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ].map((opt) => {
                    const active = answers[q.id] === opt.value;
                    return (
                      <button
                        type="button"
                        key={opt.label}
                        onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt.value }))}
                        style={{
                          padding: '6px 16px',
                          borderRadius: 999,
                          border: active ? '1px solid #5b21b6' : '1px solid #d8d6e6',
                          background: active ? '#5b21b6' : '#fff',
                          color: active ? '#fff' : '#3f3a5e',
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: 'pointer',
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {result && (
        <div
          style={{
            marginTop: 16,
            background: result.bg,
            border: `1px solid ${result.tone}33`,
            borderRadius: 12,
            padding: '16px 18px',
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: result.tone,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 6,
            }}
          >
            {result.label}
          </div>
          <p style={{ margin: 0, color: '#1f1240', fontSize: 14, lineHeight: 1.55 }}>{result.verdict}</p>
          <p style={{ margin: '10px 0 0', color: result.tone, fontWeight: 600, fontSize: 14 }}>{result.cta}</p>
        </div>
      )}
    </div>
  );
}
